import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

export default function Login() {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let user;
      if (mode === "login") user = await login(email, password);
      else user = await register(name, email, password);
      toast.success(`Bienvenue, ${user.name} !`);
      const dest = location.state?.from || (user.role === "admin" ? "/admin" : "/studio");
      navigate(dest);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B0D12] text-white">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 30% 20%, #FF5500, transparent 45%), radial-gradient(circle at 70% 80%, #6366F1, transparent 45%)" }} />
        <div className="relative z-10">
          <Logo className="h-10 w-10" withText textClass="text-2xl text-white" />
        </div>
        <div className="relative z-10 space-y-4">
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Ton studio.<br />Zéro serveur.<br />100% à toi.</h2>
          <p className="text-slate-300 max-w-sm">28 pistes, analyse BPM & tonalité, mastering LUFS, export WAV 32-bit — tout tourne dans ton navigateur, hors-ligne.</p>
          <div className="flex gap-3 font-mono text-xs text-slate-400 pt-4">
            <span className="px-3 py-1 rounded-full border border-slate-700">AUCUNE CLÉ</span>
            <span className="px-3 py-1 rounded-full border border-slate-700">AUCUN SERVEUR</span>
            <span className="px-3 py-1 rounded-full border border-slate-700">HORS-LIGNE</span>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500 font-mono">© 2026 TUBMIX STUDIO</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.form
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="auth-form">
          <div className="lg:hidden mb-4"><Logo className="h-9 w-9" withText textClass="text-xl text-white" /></div>
          <h1 className="font-heading text-3xl font-extrabold">{mode === "login" ? "Connexion" : "Créer un compte"}</h1>
          <p className="text-slate-400 text-sm">{mode === "login" ? "Accède à ton studio et ton historique." : "Rejoins TUBMIX et commence à mixer."}</p>

          {mode === "register" && (
            <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Nom d'artiste" className="w-full bg-[#131722] border border-[#262C3A] rounded-xl px-4 py-3 outline-none focus:border-[#FF5500] transition-colors" />
          )}
          <input data-testid="auth-email-input" type={mode === "register" ? "email" : "text"} value={email} onChange={(e) => setEmail(e.target.value)} required placeholder={mode === "register" ? "Email" : "Email ou identifiant"} className="w-full bg-[#131722] border border-[#262C3A] rounded-xl px-4 py-3 outline-none focus:border-[#FF5500] transition-colors" />
          <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Mot de passe" className="w-full bg-[#131722] border border-[#262C3A] rounded-xl px-4 py-3 outline-none focus:border-[#FF5500] transition-colors" />

          <button data-testid="auth-submit-button" disabled={busy} className="w-full bg-[#FF5500] hover:bg-[#E04800] text-white font-semibold rounded-xl py-3 transition-colors disabled:opacity-50">
            {busy ? "..." : mode === "login" ? "Se connecter" : "Créer mon compte"}
          </button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-[#262C3A]" />
            <span className="text-xs text-slate-500">ou</span>
            <div className="flex-1 h-px bg-[#262C3A]" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button type="button" data-testid="google-signin-button" onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white text-[#0F172A] font-semibold rounded-xl py-3 hover:bg-slate-100 transition-colors">
            <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden>
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            Continuer avec Google
          </button>

          <button type="button" data-testid="auth-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-sm text-slate-400 hover:text-white transition-colors">
            {mode === "login" ? "Pas encore de compte ? Inscris-toi" : "Déjà un compte ? Connecte-toi"}
          </button>
          <button type="button" onClick={() => navigate("/")} className="w-full text-xs text-slate-600 hover:text-slate-400 transition-colors">← Retour à l'accueil</button>
        </motion.form>
      </div>
    </div>
  );
}
